<template>
  <div
    v-if="!isLoading && homeButtons"
    class="home-activities__background"
    data-testid="home-activities"
  >
    <div class="home-activities">
      <Heading :level="3">
        {{ homeButtons.title }}
      </Heading>
      <div class="home-activities__card-container">
        <CardCounter
          v-for="(button, index) in homeButtons.buttons"
          :key="index"
          class="home-activities__card-container__item"
          :icon="button.icon"
          :badge-counter="button.total"
          :label="button.label"
          @click="() => handleClick(button.type)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import { Heading } from '@warrenbrasil/nebraska-web';
import { CardCounter } from '@/modules/common/components/cards/CardCounter';

import {
  IHomeButtonsV2,
  getButtonsV2,
  EButtonTypeV2,
  getButtonsV3,
  IHomeButtonsV3,
  EButtonTypeV3
} from '@/modules/home/services/consolidation';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const router = useRouter();

interface Emits {
  (event: 'open-register-panel'): void;
  (event: 'open-orders-panel', type: string): void;
}

const emit = defineEmits<Emits>();

const isLoading = ref(true);
const homeButtons = ref<IHomeButtonsV2 | IHomeButtonsV3 | void>();

const isEnabledPortabilityExitMenuItem = computed(() => {
  return FeatureFlagsModule.isEnabled(FeatureFlags.PORTABILITY_EXIT_MENU_ITEM);
});

async function fetchButtons() {
  try {
    isLoading.value = true;

    if (isEnabledPortabilityExitMenuItem.value) {
      homeButtons.value = await getButtonsV3();
    } else {
      homeButtons.value = await getButtonsV2();
    }
  } finally {
    isLoading.value = false;
  }
}

function handleClick(type: EButtonTypeV2 | EButtonTypeV3) {
  if (isEnabledPortabilityExitMenuItem.value) {
    if (Object.values(EButtonTypeV3).includes(type as EButtonTypeV3)) {
      switch (type) {
        case EButtonTypeV3.APPROVALS:
          router.push({ name: 'investmentSuggestions' });
          break;
        case EButtonTypeV3.REGISTER:
          emit('open-register-panel');
          break;
        case EButtonTypeV3.INVESTMENT:
          emit('open-orders-panel', type);
          break;
        case EButtonTypeV3.REDEMPTION:
          emit('open-orders-panel', type);
          break;
        case EButtonTypeV3.PORTABILITY_REQUESTS:
          window.location.assign('/products/#/portability/requests');
          break;
        case EButtonTypeV3.PORTABILITY_IN_PROGRESS:
          window.location.assign('/products/#/portability');
          break;
        default:
          break;
      }
      return;
    }
  }

  if (Object.values(EButtonTypeV2).includes(type as EButtonTypeV2)) {
    switch (type) {
      case EButtonTypeV2.IN_PORTABILITY:
        window.location.assign('/products/#/portability');
        break;
      case EButtonTypeV2.OUT_PORTABILITY:
        router.push({ name: 'portabilityRequests' });
        break;
      case EButtonTypeV2.REQUESTS_PORTABILITY:
        window.location.assign('/products/#/portability/requests');
        break;
      case EButtonTypeV2.APPROVALS:
        router.push({ name: 'investmentSuggestions' });
        break;
      case EButtonTypeV2.REGISTER:
        emit('open-register-panel');
        break;
      case EButtonTypeV2.INVESTMENT:
        emit('open-orders-panel', type);
        break;
      case EButtonTypeV2.REDEMPTION:
        emit('open-orders-panel', type);
        break;
      default:
        break;
    }
  }
}

onMounted(fetchButtons);
</script>

<style lang="less" scoped>
.home-activities {
  .container-width(@size-spacing-x1200, @size-spacing-x1200);

  &__background {
    background-color: @background-tertiary;
  }

  &__card-container {
    margin-top: @size-spacing-x600;
    display: flex;
    flex-wrap: wrap;
    gap: @size-spacing-x300;

    &__item {
      flex: 1 1 160px;
      max-width: 195px;
    }

    :deep(.card-counter) {
      background-color: @background-secondary;

      &:hover {
        background-color: @background-hover;
      }
    }
  }
}
</style>
